import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from '@parrot-software/design-system/icons';

import type { IconName } from './DynamicIcon';
import { DynamicIcon } from './DynamicIcon';
import { usePermissionsDebug } from '@personnel/presentation/hooks/usePermissionsDebug';

/**
 * Permission Accordion Component
 *
 * Collapsible container for permissions.
 * Useful for grouping related permissions that can be hidden/shown.
 *
 * @param {PermissionAccordionProps} props - Component properties
 * @returns {React.ReactElement} Permission accordion component
 *
 * @example
 * <PermissionAccordion
 *   name="Gestión de Cuentas"
 *   description="Permite acceder y configurar permisos relacionados con cuentas"
 *   defaultOpen={false}
 * >
 *   <PermissionCheckbox ... />
 *   <PermissionCheckbox ... />
 * </PermissionAccordion>
 */

interface PermissionAccordionProps {
  /** Accordion name */
  name: string;
  /** Optional description */
  description?: string;
  /** Icon name (optional) */
  iconName?: IconName;
  /** Whether accordion is open by default */
  defaultOpen?: boolean;
  /** Child permissions */
  children: React.ReactNode;
}

const styles = {
  container: 'flex flex-col gap-2',
  header: {
    button: 'flex items-start gap-3 w-full cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors',
    icon: 'size-5 text-foreground mt-0.5 shrink-0',
    textWrapper: 'flex flex-col gap-1 flex-1',
    title: 'text-sm font-medium text-foreground text-left',
    description: 'text-sm text-muted-foreground text-left',
    chevron: 'size-5 text-muted-foreground mt-0.5 shrink-0',
  },
  content: {
    wrapper: 'overflow-hidden transition-all duration-200',
    inner: 'flex flex-col gap-2 ml-8 pt-2',
  },
  debug: 'text-xs font-mono p-2 rounded-md bg-cyan-50 border border-cyan-200 text-cyan-700 mt-1 inline-block',
};

export const PermissionAccordion = ({
  name,
  description,
  iconName,
  defaultOpen = false,
  children,
}: PermissionAccordionProps): React.ReactElement => {
  const { isDebugMode } = usePermissionsDebug();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={styles.container}>
      {/* Accordion Header */}
      <button className={styles.header.button} onClick={handleToggle} type="button">
        {iconName && (
          <div className={styles.header.icon}>
            <DynamicIcon className="size-5 text-foreground" name={iconName} />
          </div>
        )}

        <div className={styles.header.textWrapper}>
          <span className={styles.header.title}>{name}</span>
          {description && <p className={styles.header.description}>{description}</p>}
          {/* Debug info - Only shown in development when enabled via console */}
          {isDebugMode && <span className={styles.debug}>🎹 ACCORDION</span>}
        </div>

        <div className={styles.header.chevron}>{isOpen ? <ChevronUp /> : <ChevronDown />}</div>
      </button>

      {/* Accordion Content */}
      {isOpen && (
        <div className={styles.content.wrapper}>
          <div className={styles.content.inner}>{children}</div>
        </div>
      )}
    </div>
  );
};
